import { FormItem, Input, Textarea } from '@vkontakte/vkui';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import type { Session } from '../../api/session';
import { FORM_MODERATOR_ROLES } from '../../api/types';
import { t } from '../../i18n';
import { LinkIcon } from '../../shared/icons';
import type { ApiImage } from '../../shared/lib/media';
import {
  Action,
  ActionRow,
  FieldLabel,
  Gutter,
  ImageField,
  Notice,
  Section,
  SectionTitle,
} from '../../shared/ui';

/**
 * Карточка справочника внешек. Картинка приходит как {id, url, thumbUrl} с
 * адресами /api/media/:id — общий вид для всего проекта, ключей хранилища в
 * ответе нет.
 */
export interface FaceClaimItem {
  id: number;
  name: string;
  link: string | null;
  note: string | null;
  image: ApiImage | null;
}

/**
 * Кто правит базу внешек. Зеркало FACE_CLAIM_EDITORS из
 * apps/api/src/common/utils/roles.ts: модератор анкет и суперадмин. Остальным
 * раздел доступен только на чтение — это не догадка клиента, а тот же
 * список ролей, что проверяет guard.
 */
export function canEditFaceClaims(session: Session): boolean {
  return session.user !== null && FORM_MODERATOR_ROLES.includes(session.user.role);
}

/** Пределы из FaceClaimBodyDto: обрезаем на вводе, чтобы не ловить 400 после отправки. */
const NAME_MAX = 200;
const LINK_MAX = 500;
const NOTE_MAX = 2000;

interface FaceClaimEditProps {
  /** null = создаём новую карточку. */
  initial: FaceClaimItem | null;
  onCancel: () => void;
  onSaved: (card: FaceClaimItem) => void;
  /**
   * Загрузка файла требует права записи: маршрут /api/media/upload помечен
   * RequiresWrite, а роль модератора сама по себе его не даёт.
   */
  canUploadPhoto: boolean;
}

/**
 * Форма создания и правки. Не панель: собственного маршрута у неё нет, её
 * показывают вместо содержимого списка или карточки. На сервере PATCH со
 * слиянием: пропущенное поле остаётся прежним, и картинку поэтому шлют,
 * только когда её меняли — чужую загрузку сервер не примет.
 *
 * Обёртки <form> нет намеренно: внутри неё кнопка «Отмена» по умолчанию
 * отправляла бы форму, а сохранение и так висит на onClick.
 */
export function FaceClaimEdit({ initial, onCancel, onSaved, canUploadPhoto }: FaceClaimEditProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [link, setLink] = useState(initial?.link ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [imageId, setImageId] = useState<string | null>(initial?.image?.id ?? null);
  /**
   * Правка отправляет картинку, ТОЛЬКО если её трогали. Иначе редактор пересылал
   * бы чужую картинку обратно, а сервер вправе принять лишь свою — и правка
   * чужой карточки падала бы с 403.
   */
  const [imageChanged, setImageChanged] = useState(false);

  const [nameError, setNameError] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('face.error.nameRequired'));
      return;
    }

    setNameError('');
    setError('');
    setSaving(true);

    try {
      const body = {
        name: trimmed,
        link: link.trim() || null,
        note: note.trim() || null,
        // При создании картинка идёт всегда, при правке — только если её меняли.
        ...(initial && !imageChanged ? {} : { imageId }),
      };

      const saved = initial
        ? await api.patch<FaceClaimItem>(`/api/face-claims/${initial.id}`, body)
        : await api.post<FaceClaimItem>('/api/face-claims', body);

      onSaved(saved);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
      setSaving(false);
    }
  };

  return (
    <Section>
      {/* Фото стоит первым: в справочнике оно и есть запись, а имя со ссылкой —
          подписи к нему. */}
      <SectionTitle>{t('face.section.photo')}</SectionTitle>

      {/* Подпись поля остаётся его собственной: ImageField отдаёт её же в alt
          картинки, и «Фото внешки» под заголовком раздела — не дубль, а то, что
          прочитает читалка с экрана. */}
      <FormItem
        top={<FieldLabel>{t('face.field.photo')}</FieldLabel>}
        bottom={canUploadPhoto ? undefined : t('face.hint.uploadClosed')}
      >
        <ImageField
          variant="hero"
          label={t('face.field.photo')}
          imageId={imageId}
          disabled={!canUploadPhoto}
          onChange={(next) => {
            setImageId(next);
            setImageChanged(true);
          }}
        />
      </FormItem>

      <SectionTitle>{t('face.section.record')}</SectionTitle>

      {/* Счётчик в шапке поля, а не в подсказке снизу: там уже висит пояснение,
          а без счётчика ввод просто перестаёт набираться на пределе. */}
      <FormItem
        top={
          <FieldLabel required counter={`${name.length} / ${NAME_MAX}`}>
            {t('face.field.name')}
          </FieldLabel>
        }
        status={nameError ? 'error' : 'default'}
        bottom={nameError}
      >
        <Input
          value={name}
          maxLength={NAME_MAX}
          placeholder={t('face.placeholder.name')}
          onChange={(event) => setName(event.target.value)}
        />
      </FormItem>

      <FormItem
        top={
          <FieldLabel counter={`${link.length} / ${LINK_MAX}`} hint={t('face.hint.link')}>
            {t('face.field.link')}
          </FieldLabel>
        }
      >
        {/* Цепь внутри рамки, как в макете: поле со ссылкой узнаётся по
            значку раньше, чем прочитана подпись. */}
        <Input
          before={<LinkIcon tone="muted" />}
          value={link}
          maxLength={LINK_MAX}
          placeholder="https://"
          onChange={(event) => setLink(event.target.value)}
        />
      </FormItem>

      <FormItem
        top={
          <FieldLabel counter={`${note.length} / ${NOTE_MAX}`} hint={t('face.hint.note')}>
            {t('common.field.note')}
          </FieldLabel>
        }
      >
        <Textarea
          rows={3}
          value={note}
          maxLength={NOTE_MAX}
          onChange={(event) => setNote(event.target.value)}
        />
      </FormItem>

      {error ? (
        <Gutter>
          <Notice tone="error">{error}</Notice>
        </Gutter>
      ) : null}

      <Gutter>
        {/* Сохранение забирает полосу, отмена остаётся узкой: две равные
            кнопки читались как равный выбор. */}
        <ActionRow>
          <Action
            tone="action"
            size="l"
            stretched
            loading={saving}
            onClick={() => {
              void save();
            }}
          >
            {t('common.action.save')}
          </Action>
          <Action tone="quiet" size="l" onClick={onCancel}>
            {t('common.action.cancel')}
          </Action>
        </ActionRow>
      </Gutter>
    </Section>
  );
}
